import { RoomConnection } from "@topzle/realtime";
import type { RoomMessage } from "@topzle/types";
import { Bot, MessageCircle, Swords, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { roomTurnParticipantId, shouldNotifyRoomParticipant } from "../roomNotifications";
import { useAppStore } from "../state";

interface AwayNotice {
  id: string;
  title: string;
  detail: string;
  kind: "chat" | "turn" | "bot";
}

/**
 * Keeps the most recently joined room online while the player browses Topzle.
 * The room server separately receives activeInRoom=false, so matchmaking and
 * opponents can distinguish an online member from someone looking at the room.
 */
export function RoomActivityBridge() {
  const location = useLocation();
  const room = useAppStore((state) => state.room);
  const updateRoomSnapshot = useAppStore((state) => state.updateRoomSnapshot);
  const [notice, setNotice] = useState<AwayNotice | null>(null);
  const lastTurn = useRef<string | null>(null);
  const botNoticeShown = useRef(false);
  const roomRef = useRef(room);
  roomRef.current = room;

  const roomId = room?.roomId;
  const participantId = room?.participantId;
  const websocketUrl = room?.websocketUrl;
  const reconnectToken = room?.reconnectToken;

  const viewingRoom = roomId ? location.pathname === `/room/${roomId}` : false;

  useEffect(() => {
    if (!roomId || !participantId || !websocketUrl || !reconnectToken || viewingRoom) return;
    const connection = new RoomConnection({
      url: websocketUrl,
      roomId,
      participantId,
      reconnectToken,
      onState: (state) => {
        if (state.snapshot) updateRoomSnapshot(roomId, state.snapshot);
        if (state.status === "connected") connection.send("room.presence", { activeInRoom: false });
      },
      onMessage: (message: RoomMessage) => {
        const currentRoom = roomRef.current;
        if (!currentRoom) return;
        if (!shouldNotifyRoomParticipant(currentRoom.snapshot, participantId)) return;
        if (message.type === "chat.message" && message.senderId !== participantId) {
          const payload = message.payload as { text?: string; history?: boolean };
          if (!payload.history && payload.text)
            setNotice({
              id: message.id,
              title: "New room message",
              detail: payload.text,
              kind: "chat",
            });
          return;
        }
        if (!["game.state", "game.started", "game.resumed"].includes(message.type)) return;
        const payload = message.payload as {
          gameId?: string;
          state?: Parameters<typeof roomTurnParticipantId>[1];
        };
        const gameId = payload.gameId ?? currentRoom.snapshot.currentGameId;
        const turn = roomTurnParticipantId(gameId, payload.state ?? currentRoom.snapshot.gameState);
        const versusBot = currentRoom.snapshot.participants.some(
          (participant) => participant.role === "ai" || participant.id.startsWith("ai:"),
        );
        if (turn === participantId && lastTurn.current !== participantId) {
          if (!versusBot || !botNoticeShown.current) {
            setNotice({
              id: message.id,
              title: versusBot ? "Your bot game is paused" : "Your turn",
              detail: versusBot
                ? "Return whenever you are ready. The bot and game timer are waiting."
                : "Your opponent played. Open the room to make your move.",
              kind: versusBot ? "bot" : "turn",
            });
            if (versusBot) botNoticeShown.current = true;
          }
        }
        lastTurn.current = turn;
      },
    });
    connection.connect();
    return () => connection.close();
  }, [participantId, reconnectToken, roomId, updateRoomSnapshot, viewingRoom, websocketUrl]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 9_000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  if (!roomId || viewingRoom || !notice) return null;
  const Icon = notice.kind === "chat" ? MessageCircle : notice.kind === "bot" ? Bot : Swords;
  return (
    <aside className={`away-room-notice ${notice.kind}`} aria-live="polite">
      <Icon />
      <span>
        <strong>{notice.title}</strong>
        <small>{notice.detail}</small>
      </span>
      <Link to={`/room/${roomId}`}>Open room</Link>
      <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss notification">
        <X />
      </button>
    </aside>
  );
}
